
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { enableProdMode, ErrorHandler, provideZonelessChangeDetection, importProvidersFrom } from "@angular/core";

import { VncTranslateHttpLoader, HttpLoaderFactory } from "./app/app.module";
import { environment } from "./environments/environment";
import { ActionCableService } from "angular2-actioncable";
import { AuthGuard, AppRoutingModule } from "./app/app-routing.module";
import { AvailableRoutes } from "./app/common/providers/route.guards";
import { Broadcaster } from "./app/common/providers/broadcaster.service";
import { ErrorService } from "./app/common/providers/error-service";
import { SuccessService } from "./app/common/providers/success-service";
import { VNCActionWheelMenuService, GlobalSearchService, DocumentPreviewModule, GlobalSearchModule } from "@uxf/vnc-library";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { provideHttpClient, withInterceptorsFromDi, HttpClient } from "@angular/common/http";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { BrowserModule, bootstrapApplication } from "@angular/platform-browser";
import { SharedModule } from "./app/shared/shared.module";
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { CommonUtil } from "./app/common/utils/common.utils";
import { StoreModule } from "@ngrx/store";
import { reducers, reset } from "./app/reducers";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { EffectsModule } from "@ngrx/effects";
import { NgxOnlyOfficeModule } from "ngx-onlyoffice";
import { AppComponent } from "./app/app.component";
import { AppConstants } from "./app/common/utils/app-constants";
import { StorageService } from "./app/common/services/storage.service";
import * as Sentry from "@sentry/angular";

if (environment.sentryDsn) {
  Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.production ? "production" : "development",
  });
}

const bUrl = CommonUtil.getBaseUrl();
const path = bUrl.endsWith("/") ? "assets/i18n/" : "/assets/i18n/";



setupTheme();

if (environment.production || environment.isCordova || environment.isElectron) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(CommonModule, FormsModule, BrowserAnimationsModule, BrowserModule, SharedModule, AppRoutingModule, TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }), SharedModule.forRoot(), StoreModule.forRoot(reducers, { metaReducers: [reset], runtimeChecks: {
                strictStateImmutability: false,
                strictActionImmutability: false,
            } }),
        // Module-style to match StoreModule.forRoot above: provideEffects() injects NgRx's
        // ROOT_STORE_PROVIDER, which only provideStore() supplies, so it cannot be mixed
        // with a StoreModule.forRoot bootstrap (STR-07).
        // Root registration only bootstraps the effects runner; the task effects themselves are
        // registered via forFeature in TaskModule so they share its injector's service
        // instances (see task.module.ts, STR-07).
        EffectsModule.forRoot([]), !environment.production ? StoreDevtoolsModule.instrument({ maxAge: 50 }) : [], DocumentPreviewModule, GlobalSearchModule.forRoot({
            serverURL: localStorage.getItem("serverURL") !== null && localStorage.getItem("serverURL") !== "null" ? localStorage.getItem("serverURL") : "",
            searchAPI: "",
            environment: {
                isCordova: environment.isCordova,
                isElectron: environment.isElectron
            },
            token: localStorage.getItem("token"),
        }), NgxOnlyOfficeModule),
        ActionCableService,
        AuthGuard,
        AvailableRoutes,
        Broadcaster,
        ErrorService,
        SuccessService,
        VNCActionWheelMenuService,
        GlobalSearchService,
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: "outline" } },
        provideHttpClient(withInterceptorsFromDi()),
        provideZonelessChangeDetection(),
        { provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false }) }
    ]
})
  .catch(err => console.error(err));

function loadExternalStyle(styleUrl: string) {
  return new Promise((resolve, reject) => {
    let styleElement = document.createElement("link");
    styleElement.rel = "stylesheet";
    styleElement.type = "text/css";
    styleElement.href = styleUrl;
    styleElement.onload = function() {
      resolve(null);
    };
    document.head.appendChild(styleElement);
    styleElement = null;
  });
}

function loadExternalScript(scriptUrl: string) {
  return new Promise(resolve => {
    let scriptElement = document.createElement("script");
    scriptElement.src = scriptUrl;
    scriptElement.onload = resolve;
    document.body.appendChild(scriptElement);
    scriptElement = null;
  });
}

function setupTheme() {
  if (environment.isCordova) {
    loadExternalScript("cordova.js");
    // remove non-cordova-div
    // document.getElementById("non-cordova-only").outerHTML = "";
    loadExternalScript("assets/js/SocialSharing.js");
  } else {
    // document.getElementById("cordova-only").outerHTML = "";
  }

  console.log("[main.ts] environment", environment);
  document.title = environment.title;
  applyStoredDarkMode();
}

function applyStoredDarkMode() {
  if (StorageService.getLocalItem(AppConstants.TASK_DARK_MODE) === "1") {
    document.querySelector("body")?.classList.add("active-dark-theme");
  }
}
